import type { Match } from '@uefadigital/panenka';
import {
  PkAvatar,
  PkIcon,
  PkIdentifier,
  PkTable,
  PkTableBody,
  PkTableCell,
  PkTableHeader,
  PkTableHeaderCol,
  PkTableRow,
} from '@uefadigital/panenka-react';
import clsx from 'clsx';
import i18n from 'i18next';
import type { ReactNode } from 'react';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import LocationObserver from '../LocationObserver';

import birthdayPresentBox from 'assets/birthdayPresentBox.svg';
import type { PlayerStatus, SquadGroup, Statistic } from 'types/ApiResponseInterfaces';
import type { ConfigCompetition } from 'utils/config';
import { getPlayerLink, handleGoToLink } from 'utils/helpers';
import { translateEntity } from 'utils/translateEntity';

import * as style from './style.module.css';

interface Team {
  id?: string;
  internationalName?: string;
}

interface SquadDetailsTableData {
  elementIndex: {
    index: number;
    length: number;
  };
  squad: SquadGroup;
  competition?: ConfigCompetition;
  isPDFPrint?: boolean;
  matchData: Match;
  teams?: Array<Team> | undefined;
}

interface HeadingCellData {
  name?: string;
  value: string;
  translations?: {
    name: Record<string, string>;
  };
  subname?: string;
  time?: string;
  isCurrent?: boolean;
}

const playerBirthday = (playerDate: string) => {
  const playerBirthdayDate = playerDate.substring(0, playerDate.lastIndexOf(' '));

  const dateNow = new Date()
    .toLocaleDateString(i18n.language, {
      day: 'numeric',
      month: 'short',
    })
    .replace(/ /g, ' ');

  return dateNow === playerBirthdayDate ? <img src={birthdayPresentBox} className="pk-ml--xs" /> : '';
};

const generatePlayerName = (firstName: string, lastName: string) =>
  `${firstName.length ? `${firstName} ` : ''}${lastName}`;

const PlayerAvailability = ({ playerStatus }: { playerStatus: PlayerStatus | undefined }) => {
  const { t } = useTranslation();

  if (playerStatus?.isSuspended) {
    return (
      <p slot="primary" className="pk-d--flex pk-align-items--center pk-mb--0">
        <PkIcon name="football-suspended" size={20} color="support-04A" className="pk-mr--xs2" />
        <span className="pk-text--text-02 pk-font-size--xs pr-font-size--10 pk-font-weight--normal pk-line-height--md">
          {t('suspended')}
        </span>
      </p>
    );
  }

  if (playerStatus?.isBooked) {
    return (
      <p slot="primary" className="pk-d--flex pk-align-items--center pk-mb--0 pk-text--wrap pk-pr--m pr-pr--0">
        <PkIcon name="football-card-single-card" size={21} className="pk-text--support-05 pk-mr--xs2" />
        <span className="pk-text--text-02 pk-font-size--xs pr-font-size--10 pk-font-weight--normal pk-line-height--md">
          {t('misses_next_match_if_booked')}
        </span>
      </p>
    );
  }

  return null;
};

export const SquadDetailsTable = ({
  elementIndex,
  squad,
  competition,
  isPDFPrint = false,
  matchData,
  teams,
}: SquadDetailsTableData) => {
  const { t } = useTranslation();

  const isNational = matchData?.competition?.teamCategory === 'NATIONAL';

  const [indexCurrentSeason, setIndexCurrentSeason] = useState(0);
  const [indexAllTime, setIndexAllTime] = useState(0);
  const [indexAllUefaComp, setIndexAllUefaComp] = useState(0);
  const headingCells = useMemo(() => {
    const values = [t('name'), t(isNational ? 'club' : 'nationality'), t('date_of_birth'), t('age')];

    const headingCellsLength = values.length;

    squad.players[0].stats?.forEach((stats, idx) => {
      if (idx === 0) {
        setIndexCurrentSeason(headingCellsLength);
      } else if (idx === 1) {
        setIndexAllTime(headingCellsLength + 1);
      } else {
        setIndexAllUefaComp(headingCellsLength + 2);
      }

      values.push(stats.name);
    });

    return values;
  }, [isNational, squad.players, t]);

  const tableHeadingCells = useMemo(
    () =>
      headingCells.map((el, i) => {
        const width = !i ? 23.5 : '';
        const isNationalityCell = i === 1 ? isNational : '';

        const isGoalsStats = el === 'matches_appearance/goals' || el === 'matches_appearance/goals_conceded';

        return (
          <PkTableHeaderCol
            className={clsx(isNationalityCell, style.headerCol)}
            aligned="center"
            column-key={el}
            key={el + i}
            width={width}
            use-percentage-width
          >
            <span className="pk-font-size--m pk-text--ui-06 pr-font-size--10 pr-font-weight--400">{t(el)}</span>
            {isGoalsStats && (i === indexCurrentSeason || i === indexAllTime) && (
              <div className="pk-font-size--xs pk-text--ui-06 pr-font-size--10 pr-font-weight--400">
                {competition?.description}
              </div>
            )}
            {isGoalsStats && (
              <div className="pk-font-size--xs pk-text--ui-06 pr-font-size--10 pr-font-weight--400">
                {i === indexAllUefaComp
                  ? `${t(isNational ? 'national_team_all_time' : 'uefa_all_time')}*`
                  : i === indexCurrentSeason
                    ? t('current_season')
                    : t('all_time')}
              </div>
            )}
          </PkTableHeaderCol>
        );
      }),
    [competition?.description, headingCells, indexAllTime, indexAllUefaComp, indexCurrentSeason, isNational, t],
  );

  const displayStatisticData = useCallback(
    (statisticArr: Array<Statistic> | undefined | Array<Statistic | HeadingCellData>) => {
      return statisticArr?.map((statistic, i: number) => {
        return (
          <PkTableCell className="pk-py--xs pr-py--0" aligned="center" key={i} column-key={headingCells[i + 4]}>
            <span className="pk-text--text-01 pr-font-size--10 pr-font-weight--400">{statistic?.value}</span>
          </PkTableCell>
        );
      });
    },
    [headingCells],
  );

  const handlePlayerClick = useCallback(
    (playerLinkArgument: string | undefined) => handleGoToLink(playerLinkArgument),
    [],
  );

  const squadMap = squad.players.map((play) => {
    const playerDate = new Date(play?.player?.birthDate ?? '')
      .toLocaleDateString(i18n.language, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
      .replace(/ /g, ' ');

    const translatePlayer = t('player');

    const firstName = translateEntity(play.player)?.firstName ?? '';
    const lastName = translateEntity(play.player)?.lastName ?? translatePlayer;

    const playerName = generatePlayerName(firstName, lastName);

    const playerLink = competition && getPlayerLink(play.player?.id, competition);

    return (
      <PkTableRow
        key={play.player.id}
        onClick={() => handlePlayerClick(playerLink)}
        className={`pk-mb--m ${style.pointer}`}
      >
        <PkTableCell className="pk-py--xs pr-py--0" with-divider aligned="left" column-key={headingCells[0]}>
          <PkIdentifier alignment="left" className="pk-pl--m">
            <span slot="prefix">
              <span
                className={`${
                  play?.jerseyNumber && play.jerseyNumber < 10 ? 'pk-ml--xs' : ''
                } pk-mr--xs pk-font-size--m pk-line-height--lg pk-text--text-01 pr-font-size--10 pr-font-weight--500`}
              >
                {play.jerseyNumber}
              </span>
              <PkAvatar
                type="image"
                src={play.player.imageUrl}
                class="hydrated"
                style={{ borderRadius: '24px' }}
                className="pr-avatar-size--30"
              />
            </span>
            <span slot="primary" className="pk-text--text-01 pk-font-size--m pr-font-size--10 pr-font-weight--500">
              {playerName}
            </span>
            <PlayerAvailability playerStatus={play.status} />
          </PkIdentifier>
        </PkTableCell>
        <PkTableCell className={'pk-py--xs pr-py--0'} aligned="center" column-key={headingCells[1]}>
          <span className="pk-text--text-01 pr-font-size--10 pr-font-weight--400">
            {isNational && teams
              ? teams.find((team) => team.id === play?.player?.clubId)?.internationalName
              : translateEntity(play?.player)?.countryName || t('unknown')}
          </span>
        </PkTableCell>
        <PkTableCell className="pk-py--xs pr-py--0" aligned="center" column-key={headingCells[2]}>
          <span className="pk-text--text-01 pr-font-size--10 pr-font-weight--400">{playerDate}</span>
          {playerBirthday(playerDate)}
        </PkTableCell>
        <PkTableCell className="pk-py--xs pr-py--0" aligned="center" column-key={headingCells[3]}>
          <span className="pk-text--text-01 pr-font-size--10 pr-font-weight--400">{play.player.age}</span>
        </PkTableCell>
        {displayStatisticData(play?.stats)}
      </PkTableRow>
    );
  });

  const squadTable = () => (
    <PkTable
      className={elementIndex.index === elementIndex.length - 1 ? '' : 'pk-mb--l'}
      sort-by="displayName"
      sort-order="desc"
      cell-vertical-pad="24"
    >
      <PkTableHeader>
        <PkTableRow
          className="pk-mb--l"
          style={{
            display: 'table-block',
          }}
        >
          {tableHeadingCells}
        </PkTableRow>
      </PkTableHeader>
      <PkTableBody>{squadMap}</PkTableBody>
    </PkTable>
  );

  const addLazyLoad = (childs: ReactNode) => {
    if (isPDFPrint) {
      return <Fragment>{childs}</Fragment>;
    }

    return <LocationObserver>{childs}</LocationObserver>;
  };

  return (
    <div className="pk-m--0 pk-p--0 pr-avoid-break--inside">
      <div className="pk-text--text-01 pk-font-size--xl pr-mb--xs2 pk-mb--l pr-font-size--10 pr-font-weight--700 pk-font--comp-regular">
        {translateEntity(squad)?.fieldPosition}
      </div>
      {addLazyLoad(squadTable())}
    </div>
  );
};
